// CTE add/remove/rename planner. Each entry point produces a flat
// PlannedByteEdit[] over the original source. The CTE byte-range scan
// is internal to this module — it mirrors `cteScope.ts`'s walker but
// surfaces the additional offsets (WITH keyword, name range, definition
// boundaries, comma positions) the applicator needs.
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § "Slice: Edit — add/remove/rename CTE".

import type {
  ParsedBuffer, PlannedByteEdit, QueryStructure, StructuralEdit,
} from '../types';
import { skipContextFor, type SkipContext } from '../classify/tokenSkip';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import type { IdentifierFolding, IdentifierRef } from '../../sql-grammar/model';
import {
  buildParsedDocumentFromSource,
  getDocumentStructure,
} from '../../sql-grammar/document';
import { foldIdentifier, readQualifiedName } from '../scope/identifiers';
import { resolveIdentifier } from '../identifier/resolveIdentifier';
import { cteSelfVisibility } from '../scope/cteRecursion';
// SED-7's relation-use-site gate now lives in `scope/relationSlot.ts` so the
// navigational rename feature can share the identical primitive. Default
// options reproduce this module's original behaviour exactly.
import { relationSlotKind } from '../scope/relationSlot';

import type { EditCtx } from './applyStructuralEdit';
import { deleteEdit, insertEdit } from './applyStructuralEdit.shared';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

interface CteRange {
  readonly definitionStart: number;
  readonly definitionEnd: number;
  readonly nameStart: number;
  readonly nameEnd: number;
  readonly nameValue: string;
  readonly nameQuoted: boolean;
  // Body window offsets: `bodyStart` is just inside the body's open paren,
  // `bodyEnd` is the close-paren index (exclusive end of the body content) —
  // i.e. `[bodyStart, bodyEnd)` is the text between the parens. Surfaced from
  // `parseOneCte`'s existing paren scan (Plan 10 / §2.8 S14): the recursive-aware
  // rename scan walks this window for the in-body self-reference.
  readonly bodyStart: number;
  readonly bodyEnd: number;
}

interface WithClauseInfo {
  readonly hasWith: boolean;
  readonly isRecursive: boolean;
  readonly withKeywordStart: number;
  readonly afterWithKeyword: number;
  readonly afterRecursive: number;
  readonly mainStatementStart: number;
  readonly ctes: readonly CteRange[];
  // Byte offsets of inter-CTE commas (length === max(0, ctes.length - 1)).
  readonly commas: readonly number[];
}

export function planCteEdit(
  ctx: EditCtx,
  edit: Extract<StructuralEdit, { op: 'add-cte' | 'remove-cte' | 'rename-cte' }>,
): readonly PlannedByteEdit[] {
  const info = scanWithClause(ctx);
  switch (edit.op) {
    case 'add-cte':
      return planAddCte(ctx, info, edit);
    case 'remove-cte':
      return planRemoveCte(ctx, info, edit.index);
    case 'rename-cte':
      return planRenameCte(ctx, info, edit.index, edit.newName);
  }
}

function planAddCte(
  ctx: EditCtx,
  info: WithClauseInfo,
  edit: Extract<StructuralEdit, { op: 'add-cte' }>,
): readonly PlannedByteEdit[] {
  const cols = edit.columns && edit.columns.length > 0 ? ` (${edit.columns.join(', ')})` : '';
  const cteText = `${edit.name}${cols} AS (${edit.bodyText})`;
  if (!info.hasWith) {
    const recPrefix = edit.isRecursive ? 'RECURSIVE ' : '';
    const insertAt = ctx.statementInfo.range.startOffset;
    return [
      insertEdit(ctx, insertAt, `WITH ${recPrefix}${cteText} `, 'add-cte:no-with'),
    ];
  }
  // WITH clause exists: insert as a new comma-separated CTE entry.
  const ctes = info.ctes;
  const last = ctes.length - 1;
  const afterIndex = edit.afterIndex;
  if (afterIndex === undefined || afterIndex >= last) {
    return [insertEdit(ctx, ctes[last].definitionEnd, `, ${cteText}`, 'add-cte:append')];
  }
  if (afterIndex < 0) {
    return [insertEdit(ctx, ctes[0].definitionStart, `${cteText}, `, 'add-cte:prepend')];
  }
  return [insertEdit(ctx, ctes[afterIndex].definitionEnd, `, ${cteText}`, 'add-cte:middle')];
}

function planRemoveCte(
  ctx: EditCtx,
  info: WithClauseInfo,
  index: number,
): readonly PlannedByteEdit[] {
  const ctes = info.ctes;
  if (!info.hasWith || index < 0 || index >= ctes.length) return [];
  if (ctes.length === 1) {
    // Drop the entire WITH [RECURSIVE] cte AS (body) prefix up through the
    // first non-whitespace byte of the main body.
    return [
      deleteEdit(
        ctx,
        info.withKeywordStart,
        info.mainStatementStart,
        'remove-cte:drop-with',
      ),
    ];
  }
  if (index === 0) {
    // Drop the first CTE plus its trailing comma + leading whitespace of
    // the next CTE's definitionStart.
    return [
      deleteEdit(
        ctx,
        ctes[0].definitionStart,
        ctes[1].definitionStart,
        'remove-cte:first',
      ),
    ];
  }
  // Drop the preceding comma + this CTE's definition.
  const prevComma = info.commas[index - 1];
  return [
    deleteEdit(
      ctx,
      prevComma,
      ctes[index].definitionEnd,
      'remove-cte:middle-or-last',
    ),
  ];
}

function planRenameCte(
  ctx: EditCtx,
  info: WithClauseInfo,
  index: number,
  newName: string,
): readonly PlannedByteEdit[] {
  const ctes = info.ctes;
  if (!info.hasWith || index < 0 || index >= ctes.length) return [];
  const target = ctes[index];
  const out: PlannedByteEdit[] = [];
  // 1. Rename the CTE's own name binding.
  out.push({
    range: rangeOf(ctx, target.nameStart, target.nameEnd),
    replacement: newName,
    rationale: 'rename-cte:definition',
  });
  // 2. Collect every candidate identifier reference whose case-folded form
  //    matches, then keep only those a scope-aware resolution confirms are
  //    genuinely references to THIS CTE (see filterToThisCte below). Two
  //    sources feed the same filter:
  //    - downstream refs: `[definitionEnd, statementEnd)` — sibling CTE bodies
  //      + the main statement (findReferences).
  //    - the in-body recursive self-reference: `[bodyStart, bodyEnd)` — scanned
  //      ONLY when the engine's self-visibility matrix says a self-reference is
  //      legal inside the body (Plan 10 / §2.8 S14). Gating the SCAN (not scope)
  //      is what keeps a keyword-less pg/mysql "recursive" CTE — whose in-body
  //      same-name token is a base-table reference the engine rejects as
  //      recursion — from being rewritten: those engines simply never scan the
  //      body. On mssql/oracle (keyword-less recursion) and pg/mysql `WITH
  //      RECURSIVE`, the scan runs and the self-reference is coordinated with
  //      the definition rename. `filterToThisCte` resolves each candidate the
  //      same way; resolver scope is already unconditionally self-visible, so an
  //      in-body self-reference resolves to `kind:'cte'` on the renamed binding
  //      (its `bodyRange.endOffset === definitionEnd`) and survives the filter,
  //      while an in-body alias-shadow / column / self-named alias is dropped by
  //      the shared relation-slot + alias-shadow gate.
  const selfRefs = cteSelfVisibility(ctx.options.engine, info.isRecursive)
    ? findSelfReferences(ctx, target)
    : [];
  const candidates = [...selfRefs, ...findReferences(ctx, info, target)];
  const renameRanges = filterToThisCte(ctx, target, candidates);
  for (const r of renameRanges) {
    out.push({
      range: rangeOf(ctx, r.start, r.end),
      replacement: newName,
      rationale: 'rename-cte:reference',
    });
  }
  return out;
}

/**
 * SED-7 scope-aware reference filter (mirrors NAV-1's `filterToContainingTable`
 * precedent in `renamePlanner.ts`). The lexical scanner in `findCteReferences`
 * already excludes strings/comments and dotted-suffix `schema.cte`, but it
 * still surfaces every BARE folded-name match — including same-named
 * SELECT-aliases, columns, tables, parameters, and inner-subquery CTE shadows
 * that are NOT references to this CTE. Renaming those clobbers unrelated
 * identifiers (e.g. `1 AS x` in `WITH x AS (...) SELECT 1 AS x, x.v FROM x`).
 *
 * For each lexical candidate we resolve the bare token through the existing
 * scope-aware `resolveIdentifier` and keep it ONLY when it positively resolves
 * to `kind === 'cte'` AND that CTE binding is byte-identical to the one being
 * renamed (matched on `bodyRange.endOffset === target.definitionEnd` — the
 * closing-paren offset uniquely identifies this CTE body, so an inner shadow
 * CTE re-declaring the same name in a subquery resolves to a *different*
 * body range and is correctly skipped).
 *
 * DIVERGENCE FROM NAV-1 (deliberate — do NOT "align" this with NAV-1's
 * keep-on-unresolved default): NAV-1 keeps refs whose host couldn't be
 * resolved. SED-7's acceptance is the STRICT contract "CTE rename never
 * rewrites a same-named non-CTE identifier", so we favour zero false
 * positives: a candidate that resolves to anything other than this CTE —
 * INCLUDING a candidate the resolver cannot resolve at all (returns null) —
 * is SKIPPED. Documented limitation: an unresolvable occurrence of the CTE
 * name is conservatively NOT auto-renamed; the user reviews the edit preview
 * and renames it manually if it really was a CTE reference.
 *
 * Declaration guard (two layers — `resolveIdentifier`'s CTE-first `walkCtes`
 * climbs UP parent scopes, so a token that is itself an alias *declaration*
 * (`SELECT 1 AS x`, `JOIN t x`) or a select-list alias still resolves to an
 * outer same-named CTE even though it is NOT a reference):
 *
 *   1. Structural fast-skip: any candidate whose byte range coincides with an
 *      alias declaration site collected from `ctx.structure` (FROM / JOIN
 *      aliases carry a byte-exact `alias.range`; a top-level select-item alias
 *      is the trailing identifier of its `SelectItem.range`) is skipped early.
 *      This only covers the MAIN statement + its derived-table subqueries +
 *      compound branches — it does NOT see expression-embedded scalar / IN /
 *      EXISTS subqueries nor *sibling/subsequent CTE bodies*. Used as a cheap
 *      pre-filter only; correctness does NOT depend on it being complete.
 *
 *   2. Relation-use-site gate (two arms, different safety proofs):
 *
 *      (2a) BARE relation slot — the candidate is the token immediately after
 *      FROM / JOIN / APPLY / a FROM-list comma / an opening paren (derived
 *      table / set-op branch). This position ONLY ever holds a relation
 *      reference; an alias *declaration*, select-list alias, column, or
 *      parameter is never here at ANY nesting depth. Combined with the
 *      authoritative `resolveIdentifier`→THIS-CTE result this arm is
 *      unconditionally safe across arbitrarily nested scopes, including the
 *      scalar / EXISTS subquery and sibling/subsequent CTE bodies the layer-1
 *      structural walk cannot reach. (A bare `FROM t_alias` without a `.` is
 *      not this bug class — it resolves to its own relation, not THIS CTE.)
 *
 *      (2b) DOTTED qualifier head — `x.col`. This position is NOT
 *      relation-exclusive: `x` can also be a FROM/JOIN/derived-table table
 *      ALIAS that *shadows* a same-named outer CTE. `resolveIdentifier`'s
 *      `resolveRelationKind` runs the alias-blind, CTE-first `walkCtes` before
 *      reachable tables, so a shadowing in-scope alias does NOT mask the outer
 *      CTE there and `x` mis-resolves to `kind:'cte'`. The bare-arm proof
 *      therefore does NOT cover this arm. We add an explicit alias-shadow
 *      guard: resolve the qualifier token additionally as `kind:'alias'`
 *      (option (a) — `resolveAliasKind` → `walkAliases`, which DOES walk the
 *      tracked FROM/JOIN/derived alias scope, nearest-first, up the cursor's
 *      scope chain). `scope.aliases` is fed ONLY by the alias scanner from
 *      real relation aliases (never the CTE itself), so a non-null alias
 *      result means a genuine same-named relation alias is in scope and, under
 *      SQL shadowing, owns this `x.` qualifier — the candidate is then SKIPPED.
 *      Only when NO shadowing alias is in scope does the CTE win the contest
 *      and the dotted-head candidate is kept. This makes arm (2b) safe for the
 *      flat `FROM t AS x` / `FROM t x` case and arbitrarily nested ones (the
 *      cursor-driven scope chain resolves the *nearest* binding at any depth),
 *      while a genuine `cte.col` (no shadowing alias) still renames.
 *
 * Residual limitation (conservative, by design — false-NEGATIVE not -POSITIVE):
 * when a same-named non-CTE alias/table shadows the CTE at a candidate's
 * position the candidate is conservatively NOT auto-renamed even if some
 * sibling occurrence really was a CTE reference; likewise a genuine CTE
 * reference in a non-relation slot the gate does not recognise is not
 * auto-renamed. The user renames such occurrences from the edit preview. A
 * same-named non-CTE token is NEVER rewritten — including the flat
 * `FROM t AS x` / `FROM t x` case and arbitrarily nested shadowing.
 *
 * Buffer note: `EditCtx` does not carry a `ParsedBuffer`, so we reconstruct a
 * deterministic one from the same `source`/`options` the edit operates on.
 * The SED-7 contract only needs CTE-vs-non-CTE discrimination, which is a
 * purely syntactic scope walk (no `schemaSnapshot` required) — a bare column
 * with no snapshot resolves to null and is skipped, which is the correct
 * strict-acceptance behaviour anyway.
 */
function filterToThisCte(
  ctx: EditCtx,
  target: CteRange,
  candidates: readonly { start: number; end: number }[],
): readonly { start: number; end: number }[] {
  if (candidates.length === 0) return candidates;
  const parsedDocument = buildParsedDocumentFromSource(ctx.source, ctx.options);
  const documentStructure = getDocumentStructure(parsedDocument, 'cte-rename-resolve');
  const buffer: ParsedBuffer = {
    parsedDocument,
    documentStructure,
  };
  const aliasDecls: { start: number; end: number }[] = [];
  collectAliasDeclRanges(ctx, ctx.structure, aliasDecls);
  return candidates.filter(
    (c) => candidateBindsToThisCte(buffer, ctx, target, c, aliasDecls),
  );
}

// Collect byte ranges of alias *declaration* tokens (not references) from a
// query structure and its nested derived-table subqueries / compound
// branches. FROM / JOIN aliases expose a byte-exact `alias.range`; a
// select-item alias is the trailing identifier run of its `SelectItem.range`.
function collectAliasDeclRanges(
  ctx: EditCtx,
  qs: QueryStructure,
  out: { start: number; end: number }[],
): void {
  for (const si of qs.selectItems ?? []) {
    if (!si.alias) continue;
    const r = trailingIdentRange(ctx, si.range.startOffset, si.range.endOffset);
    if (r) out.push(r);
  }
  for (const fi of qs.fromItems ?? []) {
    if (fi.alias) out.push(rangeOfBinding(fi.alias.range));
    if (fi.target.kind === 'derived-table') {
      collectAliasDeclRanges(ctx, fi.target.subquery, out);
    }
  }
  for (const j of qs.joins ?? []) {
    if (j.right.alias) out.push(rangeOfBinding(j.right.alias.range));
    if (j.right.target.kind === 'derived-table') {
      collectAliasDeclRanges(ctx, j.right.target.subquery, out);
    }
  }
  for (const b of qs.compoundBranches ?? []) collectAliasDeclRanges(ctx, b, out);
}

function rangeOfBinding(r: { startOffset: number; endOffset: number }) {
  return { start: r.startOffset, end: r.endOffset };
}

// The alias token is the last identifier run inside a select-item range
// (`expr [AS] alias`). Scan back from the trimmed end to find it.
function trailingIdentRange(
  ctx: EditCtx,
  start: number,
  endExclusive: number,
): { start: number; end: number } | null {
  const src = ctx.source;
  let end = endExclusive;
  while (end > start && /\s/.test(src[end - 1])) end--;
  if (end <= start) return null;
  let s = end;
  while (s > start && IDENT_CONT.test(src[s - 1])) s--;
  if (s >= end || !IDENT_START.test(src[s])) return null;
  return { start: s, end };
}

function candidateBindsToThisCte(
  buffer: ParsedBuffer,
  ctx: EditCtx,
  target: CteRange,
  candidate: { start: number; end: number },
  aliasDecls: readonly { start: number; end: number }[],
): boolean {
  // Layer 1 — structural fast-skip: an alias *declaration* token surfaced by
  // the (necessarily incomplete) structure walk is never a CTE reference even
  // though its name resolves to the in-scope CTE — drop it before resolving.
  if (aliasDecls.some((a) => a.start === candidate.start && a.end === candidate.end)) {
    return false;
  }
  // Layer 2 — relation-use-site gate. A genuine CTE reference is ONLY ever in
  // a relation slot; a select-list / FROM-JOIN alias declaration, a column, a
  // parameter, etc. are never in the BARE relation slot at any depth. The
  // DOTTED-head slot is NOT relation-exclusive (a table alias `x.col` shadows
  // a same-named CTE), so it carries an extra alias-shadow guard below.
  const slot = relationSlotKind(ctx.source, candidate.start, candidate.end);
  if (slot === 'none') return false;
  const node = buildCteRefNode(ctx, candidate.start, candidate.end);
  if (!node) return false;
  // Alias-shadow guard for the dotted-qualifier-head arm: a FROM/JOIN/derived
  // table alias of the same name SHADOWS the outer CTE for `x.col`, but
  // `resolveIdentifier`'s CTE-first `walkCtes` is alias-blind and would still
  // resolve `x` to the CTE. Resolve the qualifier ALSO as `kind:'alias'` —
  // `walkAliases` walks the cursor's tracked alias scope chain nearest-first
  // and `scope.aliases` only ever holds real relation aliases (never the CTE),
  // so a non-null result means a shadowing alias owns this qualifier → skip.
  if (slot === 'dotted-head' && resolveIdentifier(buffer, { ...node, kind: 'alias' })) {
    return false;
  }
  const binding = resolveIdentifier(buffer, node);
  if (!binding || binding.kind !== 'cte') return false;
  // Byte-identical body match: the closing-paren offset uniquely identifies
  // this CTE's body, so an inner-subquery shadow with the same name resolves
  // to a different bodyRange and is skipped. Fold-name is a sanity belt — it
  // is compared against `foldIdentifier`, the SAME folding that produced
  // `binding.target.name.foldedName` (engine-aware default folding), so the
  // belt cannot spuriously mismatch a genuine ref on Oracle/PG when
  // `identifierFolding` is unset (the MINOR fold-helper skew). The shared
  // lexical `foldName` in `findCteReferences` is intentionally left untouched.
  return (
    binding.target.bodyRange.endOffset === target.definitionEnd &&
    binding.target.name.foldedName ===
      foldIdentifier(target.nameValue, target.nameQuoted, ctx.options)
  );
}

/**
 * Build a single-token relation `IdentifierRef` for the bare candidate at
 * `[start,end)`. `kind: 'table'` drives `resolveIdentifier`'s CTE-first
 * resolution path (`walkCtes` → reachable tables → snapshot), so a token that
 * is really a CTE resolves to `kind: 'cte'`, while an alias/column/table/etc.
 * resolves to its own kind and is filtered out. The candidate is always a
 * bare token (the lexical scan reads exactly one identifier and the
 * `before !== '.'` guard already excluded dotted suffixes), so reading a
 * single qualified name over `[start,end)` never picks up a dotted chain.
 */
function buildCteRefNode(
  ctx: EditCtx,
  start: number,
  end: number,
): IdentifierRef | null {
  const read = readQualifiedName(ctx.source, start, end, ctx.options);
  if (!read) return null;
  return {
    qualified: read.qualified,
    range: rangeFromOffsets(ctx.lineIdx, start, end),
    kind: 'table',
  };
}

function rangeOf(ctx: EditCtx, start: number, end: number) {
  return rangeFromOffsets(ctx.lineIdx, start, end);
}

function findReferences(
  ctx: EditCtx,
  _info: WithClauseInfo,
  target: CteRange,
): readonly { start: number; end: number }[] {
  return findCteReferences({
    source: ctx.source,
    options: ctx.options,
    statementEndOffset: ctx.statementInfo.range.endOffset,
    definitionEnd: target.definitionEnd,
    cteName: target.nameValue,
    cteNameQuoted: target.nameQuoted,
  });
}

// Body-bounded self-reference candidates (Plan 10 / §2.8 S14). Runs the shared
// relation-slot-gated body scanner over `[target.bodyStart, target.bodyEnd)`;
// the caller feeds the result through the SAME `filterToThisCte` as downstream
// refs, so an in-body alias-shadow / inner-CTE shadow is still discriminated by
// the scope-aware resolution there.
function findSelfReferences(
  ctx: EditCtx,
  target: CteRange,
): readonly { start: number; end: number }[] {
  return findCteSelfReferences({
    source: ctx.source,
    options: ctx.options,
    statementEndOffset: ctx.statementInfo.range.endOffset,
    definitionEnd: target.definitionEnd,
    cteName: target.nameValue,
    cteNameQuoted: target.nameQuoted,
    bodyStart: target.bodyStart,
    bodyEnd: target.bodyEnd,
  });
}

export interface FindCteReferencesArgs {
  readonly source: string;
  readonly options: EditCtx['options'];
  /** End offset of the enclosing statement (exclusive). */
  readonly statementEndOffset: number;
  /** Offset just past this CTE's own definition — scan starts here. */
  readonly definitionEnd: number;
  /** The CTE's bare (unquoted) name value. */
  readonly cteName: string;
  /** Whether the CTE name was written quoted (case-sensitive match). */
  readonly cteNameQuoted: boolean;
}

/**
 * Pure CTE reference scanner — the single source of truth for "is this CTE
 * referenced downstream". Used by the `rename-cte` planner (to coordinate
 * renames) AND by the renderer's remove-CTE guard (to require confirmation
 * before a destructive removal). Scans subsequent CTE bodies + the main
 * statement body for case-folded identifier matches, skipping comments /
 * strings via the shared token-skip primitives (no regex parsing).
 */
export function findCteReferences(
  args: FindCteReferencesArgs,
): readonly { start: number; end: number }[] {
  const { source, options, statementEndOffset, definitionEnd } = args;
  const folded = foldName(args.cteName, args.cteNameQuoted, options.identifierFolding);
  return scanFoldedNameMatches(source, definitionEnd, statementEndOffset, folded, options);
}

/**
 * Body-bounded CTE self-reference scanner (Plan 10 / §2.8 S14). Sibling of
 * `findCteReferences`: it runs the SAME lexical scan loop, but over an explicit
 * `[bodyStart, bodyEnd)` window (just inside the body's open paren … just before
 * its close paren) AND applies the module-private `relationSlotKind` gate inline
 * so only candidates in a relation slot survive — a bare relation slot (after
 * FROM / JOIN / APPLY / LATERAL / a FROM-list comma / an open paren) or a dotted
 * qualifier head (`self.col`). A same-named COLUMN, select-list alias,
 * parameter, etc. in a non-relation slot is dropped here.
 *
 * This layer is PURELY lexical + slot: it does NOT resolve identifiers, so an
 * inner-subquery CTE that SHADOWS this name (re-declared inside the body) still
 * surfaces its relation-slot reference as a candidate here. Discriminating that
 * shadow from a genuine self-reference is the rename planner's `filterToThisCte`
 * (via `resolveIdentifier`) — this scanner deliberately stops at the lexical +
 * slot layer. Recursion DETECTION (`detectCteSelfReference`, Plan 10 T5) accepts
 * this result directly; the badge is an over-approximation only in the rare
 * in-body same-name shadow case, which the rename path still resolves correctly.
 */
export function findCteSelfReferences(
  args: FindCteReferencesArgs & {
    readonly bodyStart: number;
    readonly bodyEnd: number;
  },
): readonly { start: number; end: number }[] {
  const { source, options, bodyStart, bodyEnd } = args;
  const folded = foldName(args.cteName, args.cteNameQuoted, options.identifierFolding);
  const candidates = scanFoldedNameMatches(source, bodyStart, bodyEnd, folded, options);
  return candidates.filter((c) => relationSlotKind(source, c.start, c.end) !== 'none');
}

// Shared lexical scan: every BARE folded-name match of `folded` in `[from, to)`,
// skipping strings/comments via the token-skip primitives and excluding a
// dotted-suffix (`schema.cte` — the byte before the match is '.'). No slot gate
// — callers layer one on (findCteSelfReferences) or take every downstream
// reference (findCteReferences).
function scanFoldedNameMatches(
  source: string,
  from: number,
  to: number,
  folded: string,
  options: EditCtx['options'],
): { start: number; end: number }[] {
  const skipCtx = skipContextFor(options);
  const refs: { start: number; end: number }[] = [];
  let i = from;
  while (i < to) {
    const skipped = trySkipNonIdent(source, i, to, skipCtx);
    if (skipped >= 0) { i = skipped; continue; }
    const ident = readIdent(source, i, to);
    if (!ident) { i++; continue; }
    const matchFolded = foldName(ident.value, ident.quoted, options.identifierFolding);
    if (matchFolded === folded) {
      const before = source[ident.start - 1] ?? '';
      if (before !== '.') refs.push({ start: ident.start, end: ident.end });
    }
    i = ident.end;
  }
  return refs;
}

function foldName(name: string, quoted: boolean, fold: IdentifierFolding | undefined): string {
  if (quoted) return name;
  if (fold === 'lower') return name.toLowerCase();
  if (fold === 'upper') return name.toUpperCase();
  // 'as-stored' / 'case-insensitive' / undefined: fold to lower for matching.
  return name.toLowerCase();
}

interface IdentMatch {
  readonly value: string;
  readonly start: number;
  readonly end: number;
  readonly quoted: boolean;
}

function readIdent(source: string, from: number, end: number): IdentMatch | null {
  if (from >= end) return null;
  const c = source[from];
  if (c === '"') {
    let j = from + 1;
    while (j < end && source[j] !== '"') j++;
    if (j >= end) return null;
    return { value: source.slice(from + 1, j).replace(/""/g, '"'), start: from, end: j + 1, quoted: true };
  }
  if (c === '`') {
    let j = from + 1;
    while (j < end && source[j] !== '`') j++;
    if (j >= end) return null;
    return { value: source.slice(from + 1, j), start: from, end: j + 1, quoted: true };
  }
  if (c === '[') {
    let j = from + 1;
    while (j < end && source[j] !== ']') j++;
    if (j >= end) return null;
    return { value: source.slice(from + 1, j), start: from, end: j + 1, quoted: true };
  }
  if (!IDENT_START.test(c)) return null;
  let j = from + 1;
  while (j < end && IDENT_CONT.test(source[j])) j++;
  return { value: source.slice(from, j), start: from, end: j, quoted: false };
}

// Skip whitespace, comments, and string literals. Returns the post-skip offset
// or -1 when nothing was skipped (caller advances by 1 and tries identifier).
function trySkipNonIdent(source: string, from: number, end: number, ctx: SkipContext): number {
  if (from >= end) return -1;
  const c = source[from];
  // Whitespace
  if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
    let j = from + 1;
    while (j < end && /\s/.test(source[j])) j++;
    return j;
  }
  // Line comments
  if (c === '-' && source[from + 1] === '-') {
    let j = from + 2;
    while (j < end && source[j] !== '\n') j++;
    return j;
  }
  if (ctx.supportsHashComment && c === '#') {
    let j = from + 1;
    while (j < end && source[j] !== '\n') j++;
    return j;
  }
  // Block comments
  if (c === '/' && source[from + 1] === '*') {
    let j = from + 2;
    while (j < end && !(source[j] === '*' && source[j + 1] === '/')) j++;
    if (j < end) j += 2;
    return j;
  }
  // Single-quoted string (skip escaped quotes via doubled '')
  if (c === '\'') {
    let j = from + 1;
    while (j < end) {
      if (source[j] === '\'') {
        if (source[j + 1] === '\'') { j += 2; continue; }
        return j + 1;
      }
      j++;
    }
    return j;
  }
  return -1;
}

// eslint-disable-next-line max-lines-per-function, complexity
function scanWithClause(ctx: EditCtx): WithClauseInfo {
  const { source } = ctx;
  const stmtStart = ctx.statementInfo.range.startOffset;
  const stmtEnd = ctx.statementInfo.range.endOffset;
  const skipCtx = skipContextFor(ctx.options);

  const i = skipWsAndComments(source, stmtStart, stmtEnd, skipCtx.supportsHashComment);
  const empty: WithClauseInfo = {
    hasWith: false,
    isRecursive: false,
    withKeywordStart: stmtStart,
    afterWithKeyword: stmtStart,
    afterRecursive: stmtStart,
    mainStatementStart: stmtStart,
    ctes: [],
    commas: [],
  };
  const withWord = readWord(source, i, stmtEnd);
  if (!withWord || withWord.upper !== 'WITH') return empty;

  const withKeywordStart = withWord.start;
  let cursor = withWord.end;
  cursor = skipWsAndComments(source, cursor, stmtEnd, skipCtx.supportsHashComment);
  let isRecursive = false;
  let afterRecursive = cursor;
  const recursiveProbe = readWord(source, cursor, stmtEnd);
  if (recursiveProbe && recursiveProbe.upper === 'RECURSIVE') {
    isRecursive = true;
    cursor = recursiveProbe.end;
    afterRecursive = cursor;
    cursor = skipWsAndComments(source, cursor, stmtEnd, skipCtx.supportsHashComment);
  }

  const ctes: CteRange[] = [];
  const commas: number[] = [];
  while (cursor < stmtEnd) {
    cursor = skipWsAndComments(source, cursor, stmtEnd, skipCtx.supportsHashComment);
    const definitionStart = cursor;
    const cte = parseOneCte(source, cursor, stmtEnd, skipCtx);
    if (!cte) break;
    ctes.push({ ...cte, definitionStart });
    cursor = cte.definitionEnd;
    cursor = skipWsAndComments(source, cursor, stmtEnd, skipCtx.supportsHashComment);
    if (cursor < stmtEnd && source[cursor] === ',') {
      commas.push(cursor);
      cursor++;
      continue;
    }
    break;
  }

  const mainStatementStart = skipWsAndComments(source, cursor, stmtEnd, skipCtx.supportsHashComment);
  return {
    hasWith: true,
    isRecursive,
    withKeywordStart,
    afterWithKeyword: withWord.end,
    afterRecursive,
    mainStatementStart,
    ctes,
    commas,
  };
}

function parseOneCte(
  source: string,
  cursor: number,
  end: number,
  skipCtx: SkipContext,
): Omit<CteRange, 'definitionStart'> | null {
  let i = cursor;
  const name = readIdent(source, i, end);
  if (!name) return null;
  i = name.end;
  i = skipWsAndComments(source, i, end, skipCtx.supportsHashComment);
  if (i < end && source[i] === '(') {
    i = consumeParenGroup(source, i, end, skipCtx);
    i = skipWsAndComments(source, i, end, skipCtx.supportsHashComment);
  }
  const asWord = readWord(source, i, end);
  if (!asWord || asWord.upper !== 'AS') return null;
  i = asWord.end;
  i = skipWsAndComments(source, i, end, skipCtx.supportsHashComment);
  // Optional NOT MATERIALIZED / MATERIALIZED (PG)
  const probe = readWord(source, i, end);
  if (probe && probe.upper === 'NOT') {
    const after = skipWsAndComments(source, probe.end, end, skipCtx.supportsHashComment);
    const next = readWord(source, after, end);
    if (next && next.upper === 'MATERIALIZED') {
      i = skipWsAndComments(source, next.end, end, skipCtx.supportsHashComment);
    }
  } else if (probe && probe.upper === 'MATERIALIZED') {
    i = skipWsAndComments(source, probe.end, end, skipCtx.supportsHashComment);
  }
  if (i >= end || source[i] !== '(') return null;
  const bodyOpen = i;
  const bodyEndExclusive = consumeParenGroup(source, i, end, skipCtx);
  return {
    definitionEnd: bodyEndExclusive,
    nameStart: name.start,
    nameEnd: name.end,
    nameValue: name.value,
    nameQuoted: name.quoted,
    // `bodyEndExclusive` is one-past-the-close-paren (`definitionEnd`); the body
    // content window is just inside the open paren … the close-paren index.
    bodyStart: bodyOpen + 1,
    bodyEnd: bodyEndExclusive - 1,
  };
}

interface WordMatch { readonly upper: string; readonly start: number; readonly end: number }

function readWord(source: string, from: number, end: number): WordMatch | null {
  if (from >= end || !IDENT_START.test(source[from])) return null;
  let j = from + 1;
  while (j < end && IDENT_CONT.test(source[j])) j++;
  return { upper: source.slice(from, j).toUpperCase(), start: from, end: j };
}

function skipWsAndComments(
  source: string,
  from: number,
  end: number,
  supportsHashComment: boolean,
): number {
  let i = from;
  while (i < end) {
    const c = source[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { i++; continue; }
    const c2 = source[i + 1] ?? '';
    if (c === '-' && c2 === '-') {
      let j = i + 2;
      while (j < end && source[j] !== '\n') j++;
      i = j; continue;
    }
    // MySQL `#` line comment (engine-gated, mirrors trySkipNonIdent).
    if (supportsHashComment && c === '#') {
      let j = i + 1;
      while (j < end && source[j] !== '\n') j++;
      i = j; continue;
    }
    if (c === '/' && c2 === '*') {
      let j = i + 2;
      while (j < end && !(source[j] === '*' && source[j + 1] === '/')) j++;
      if (j < end) j += 2;
      i = j; continue;
    }
    break;
  }
  return i;
}

function consumeParenGroup(source: string, start: number, end: number, ctx: SkipContext): number {
  let depth = 0;
  let i = start;
  while (i < end) {
    const skipped = trySkipNonIdent(source, i, end, ctx);
    if (skipped >= 0) { i = skipped; continue; }
    if (source[i] === '(') { depth++; i++; continue; }
    if (source[i] === ')') {
      depth--; i++;
      if (depth === 0) return i;
      continue;
    }
    i++;
  }
  return i;
}

export type { WithClauseInfo, CteRange };
export { scanWithClause };
